import React, { useState } from "react";
import { Send } from "lucide-react";

const STATUS_META = {
  open: { label: "Open", cls: "bg-blue-100 text-blue-700" },
  booking_requested: { label: "Booking requested", cls: "bg-amber-100 text-amber-700" },
  accepted: { label: "Accepted", cls: "bg-green-100 text-green-700" },
  declined: { label: "Declined", cls: "bg-red-100 text-red-600" },
  closed: { label: "Closed", cls: "bg-gray-200 text-gray-600" },
};

export function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.open;
  return <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${m.cls}`} data-testid="enquiry-status-badge">{m.label}</span>;
}

// role = the current viewer's role ("artist" | "business" | "venue")
// events (optional, business only) = [{id,title}] to link an accepted enquiry to a real gig
export default function EnquiryThread({ enquiry, role, onSend, onStatus, events, onBook }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [evId, setEvId] = useState(enquiry?.event_id || "");
  if (!enquiry) return null;

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try { await onSend(enquiry.id, body.trim()); setBody(""); } finally { setBusy(false); }
  };
  const setStatus = async (s) => { setBusy(true); try { await onStatus(enquiry.id, s); } finally { setBusy(false); } };
  const book = async () => { setBusy(true); try { await onBook(enquiry.id, evId || null); } finally { setBusy(false); } };

  // Organisers/venues can request/accept/decline; artists can request booking / close
  const actions = role === "artist"
    ? [["booking_requested", "Request booking"], ["closed", "Close"]]
    : [["booking_requested", "Request booking"], ["accepted", "Accept"], ["declined", "Decline"], ["closed", "Close"]];

  return (
    <div data-testid="enquiry-thread">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-semibold text-[#16243b]">{enquiry.subject}</p>
          <p className="text-xs text-[#8a97a8] mt-0.5">
            {role === "artist" ? `With ${enquiry.party_name}` : `From ${enquiry.artist_name}`}
            {enquiry.proposed_date ? ` · ${enquiry.proposed_date}` : ""}
            {enquiry.proposed_fee != null ? ` · £${Number(enquiry.proposed_fee).toFixed(0)} proposed` : ""}
          </p>
        </div>
        <StatusBadge status={enquiry.status} />
      </div>

      {enquiry.event_title && (
        <div className="mb-3 rounded-xl bg-[#f0fbfb] border border-[#2ec9c9]/30 px-3 py-2 text-sm" data-testid="enquiry-booked-event">
          <span className="text-[#1fb0b0] font-semibold">Booked:</span> <span className="text-[#16243b]">{enquiry.event_title}</span>
          {enquiry.event_date ? <span className="text-[#8a97a8]"> · {new Date(enquiry.event_date).toLocaleDateString("en-GB")}</span> : ""}
        </div>
      )}

      <div className="space-y-2 max-h-64 overflow-y-auto pr-1 mb-3" data-testid="enquiry-messages">
        {(enquiry.messages || []).map((m, i) => {
          const mine = m.from_role === role;
          return (
            <div key={i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-[#2ec9c9] text-white" : "bg-[#f0f3f7] text-[#16243b]"}`}>
                <p className="text-[10px] opacity-70 mb-0.5">{m.from_name}</p>
                <p className="whitespace-pre-line">{m.body}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 mb-3">
        <input value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Write a reply…" data-testid="enquiry-reply-input"
          className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
        <button onClick={send} disabled={busy} data-testid="enquiry-send-btn" className="gp-btn-teal rounded-xl px-4 py-2 text-sm font-medium flex items-center gap-1.5 disabled:opacity-60"><Send size={14} /> Send</button>
      </div>

      <div className="flex flex-wrap gap-2">
        {actions.map(([s, label]) => (
          <button key={s} onClick={() => setStatus(s)} disabled={busy || enquiry.status === s} data-testid={`enquiry-set-${s}`}
            className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-[#5a6b80] hover:border-[#2ec9c9] hover:text-[#1fb0b0] transition-colors disabled:opacity-40">
            {label}
          </button>
        ))}
      </div>

      {(role === "business" || role === "venue") && onBook && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2" data-testid="enquiry-book-row">
          <span className="text-xs font-semibold text-[#16243b]">Link to a gig:</span>
          <select value={evId} onChange={(e) => setEvId(e.target.value)} data-testid="enquiry-book-event"
            className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs outline-none focus:border-[#2ec9c9] bg-white max-w-[220px]">
            <option value="">— pick an event —</option>
            {(events || []).map((ev) => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
          </select>
          <button onClick={book} disabled={busy} data-testid="enquiry-book-btn" className="gp-btn-teal rounded-full px-3 py-1.5 text-xs font-medium disabled:opacity-60">
            {enquiry.event_id ? "Update booking" : "Book to event"}
          </button>
          {enquiry.event_id && <button onClick={() => { setEvId(""); onBook(enquiry.id, null); }} disabled={busy} data-testid="enquiry-unbook-btn" className="text-xs text-[#8a97a8] hover:text-red-500">Unlink</button>}
        </div>
      )}
    </div>
  );
}
